/**
 * AI Entry Button
 *
 * "Create with AI" button component for various entry points.
 */

import React, { useState } from 'react';
import { AIProvider } from './context/AIContext';
import AIPromptModal from './AIPromptModal';

interface AIEntryButtonProps {
  variant?: 'primary' | 'secondary' | 'card';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  onSuccess?: (sessionId: string) => void;
}

export function AIEntryButton({
  variant = 'primary',
  size = 'medium',
  className = '',
  onSuccess,
}: AIEntryButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const buttonClasses = [
    'ai-entry-btn',
    `ai-entry-btn-${variant}`,
    `ai-entry-btn-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <button className={buttonClasses} onClick={handleOpenModal}>
        <span className="ai-entry-icon">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path
              fill="currentColor"
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
            />
          </svg>
        </span>
        <span className="ai-entry-text">Create with AI</span>
      </button>

      {isModalOpen && (
        <AIProvider>
          <AIPromptModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSuccess={onSuccess}
          />
        </AIProvider>
      )}
    </>
  );
}

/**
 * AI Card Button for template grids
 * Navigates to full AI creator page for interactive preview experience
 */
export function AICardButton({ onSuccess }: { onSuccess?: (sessionId: string) => void }) {
  const handleClick = () => {
    // Navigate to full AI creator page
    window.location.href = '/create';
  };

  return (
    <div
      className="ai-card-button"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && handleClick()}
    >
      <div className="ai-card-icon">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none">
          <path d="M9 4C9 4 9.5 6.5 10 7.5C10.5 8.5 12 9 12 9C12 9 10.5 9.5 10 10.5C9.5 11.5 9 14 9 14C9 14 8.5 11.5 8 10.5C7.5 9.5 6 9 6 9C6 9 7.5 8.5 8 7.5C8.5 6.5 9 4 9 4Z" fill="white"/>
          <path d="M17 8C17 8 17.33 9.67 17.67 10.33C18 11 19 11.5 19 11.5C19 11.5 18 12 17.67 12.67C17.33 13.33 17 15 17 15C17 15 16.67 13.33 16.33 12.67C16 12 15 11.5 15 11.5C15 11.5 16 11 16.33 10.33C16.67 9.67 17 8 17 8Z" fill="white"/>
          <path d="M11 15C11 15 11.25 16.25 11.5 16.75C11.75 17.25 12.5 17.5 12.5 17.5C12.5 17.5 11.75 17.75 11.5 18.25C11.25 18.75 11 20 11 20C11 20 10.75 18.75 10.5 18.25C10.25 17.75 9.5 17.5 9.5 17.5C9.5 17.5 10.25 17.25 10.5 16.75C10.75 16.25 11 15 11 15Z" fill="white"/>
        </svg>
      </div>
      <div className="ai-card-content">
        <h3>Create with AI</h3>
        <p>Describe your presentation and let AI generate it for you</p>
      </div>
      <div className="ai-card-badge">NEW</div>
    </div>
  );
}

export default AIEntryButton;
